import { Component, Host, Prop, h } from '@stencil/core';
import { AnchorTarget } from 'src/components';
import { SportslinePickProps } from 'src/types';

export type SportslineFreeExpertPickCardProps = {
  gameUrl: string | null;
  data: SportslinePickProps;
};

@Component({
  tag: 'sportsline-free-expert-pick-card',
  styleUrl: 'sportsline-free-expert-pick-card.css',
  shadow: true,
})
export class SportslineFreeExpertPickCard {
  /**
   * The expert pick data retrieved from the API, including game and expert details.
   */
  @Prop() pick: SportslineFreeExpertPickCardProps['data'];
  /**
   * The URL to the subscription page. If not provided, a default link will be used.
   */
  @Prop() gameUrl: string;

  /**
   * Is the user a subscriber?
   */
  @Prop() isSubscriber: boolean;

  /**
   * Game Neutral Abbreviation
   */
  @Prop() neutralGameAbbr: string = '';

  /**
   * Title of the card. If not provided, a default title will be used.
   */
  @Prop() cardTitle: string;
  /**
   * Subscribe link for the user
   */
  @Prop() subscriberLink: string;

  /**
   * Upsell text for the user
   *
   * @returns {string} - The upsell text for the user
   */
  @Prop() upsellText: string;

  /**
   * Returns the game URL target.
   */
  @Prop() gameUrlTarget: AnchorTarget = '_self';

  private get getPick() {
    if (!Boolean(this.pick)) return null;
    if (typeof this.pick === 'string') {
      try {
        return JSON.parse(this.pick);
      } catch (error) {
        console.warn('Error parsing pick data: ', error);
        return null;
      }
    }
    return this.pick;
  }

  render() {
    const isSubscriber = this.isSubscriber;
    const _pick = this.getPick;

    if (!Boolean(_pick)) {
      return (
        <Host>
          <slot></slot>
        </Host>
      );
    }

    return (
      <Host>
        <section class="sportsline-free-expert-pick">
          <div class="sportsline-free-expert-pick__header">
            <sportsline-free-expert-pick-header cardTitle={this.cardTitle}>
              {/* NOTE: only show button for subscribers */}
              <sportsline-header-expert expertStreaks={_pick.expertStreaks} expert={_pick.expert} />
              {this.gameUrl && Boolean(isSubscriber) && (
                <sportsline-button
                  variant="success"
                  size="small"
                  href={this.gameUrl}
                  target={this.gameUrlTarget}
                  style={{
                    width: 'fit-content',
                  }}
                >
                  {`See All ${this.neutralGameAbbr} Picks`}
                </sportsline-button>
              )}
            </sportsline-free-expert-pick-header>
            {Boolean(_pick.selection) && (
              <sportsline-free-expert-pick-selection pick={_pick}>
                <slot></slot>
              </sportsline-free-expert-pick-selection>
            )}
          </div>
          {/* NOTE: Only show footer for non subscriber */}
          {!isSubscriber && Boolean(this.subscriberLink) && (
            <div class="sportsline-free-expert-pick__footer">
              <sportsline-divider />
              <sportsline-free-expert-pick-footer href={this.subscriberLink} upsellText={this.upsellText} />
            </div>
          )}
        </section>
      </Host>
    );
  }
}
